import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives, unref as _unref } from "vue"

const _hoisted_1 = { class: "dish-page" }
const _hoisted_2 = { class: "page_button_header" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "dish-details" }
const _hoisted_5 = { class: "dish-title" }
const _hoisted_6 = {
  key: 0,
  class: "dish-description"
}
const _hoisted_7 = { class: "tag" }
const _hoisted_8 = { class: "exposure_progress" }
const _hoisted_9 = { class: "progress_numbers" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "last_exposure_text" }
const _hoisted_12 = {
  key: 1,
  class: "anxiety_level_over_time_container"
}
const _hoisted_13 = { class: "flex_row" }
const _hoisted_14 = { class: "loader" }
const _hoisted_15 = {
  key: 0,
  class: "error-message"
}

import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import ExposureList from '@/components/ExposureList.vue'
import AddExposureLogForm from '@/components/AddExposureLogForm.vue'
import EditDishForm from '@/components/EditDishForm.vue'
import LineChart from '@/components/LineChart.vue'
import { useStore } from 'vuex'
import { fetchWithAuth } from '@/utils/api'
import { type ExposureLog, type Dish } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'Dish',
  setup(__props) {

const store = useStore();
const apiUrl = store.getters.getApiUrl;
const route = useRoute();
const id = route.params.dish_id as string;


const showAddExposureLogForm = ref(false);
const showEditDishForm = ref(false);
const loading = ref(false);
const fetchError = ref<string | null>(null);

const getListName = (listId: string) => {
  switch (listId) {
    case 'current':
      return 'Current Exposure';
    case 'goals':
      return 'Target Dishes';
    case 'archived':
      return 'Archived Dishes';
    case 'safe':
      return 'Safe Foods';
    case 'no-go':
      return 'No-Go List';
    default:
      return 'Unknown List';
  }
}

const anxietyData = computed(() => {
  if (!dish.value || !dish.value.logs.length) return { labels: [], dataPoints: [] }

  const sortedLogs = [...dish.value.logs].sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  const labels = sortedLogs.map(log =>
    new Date(log.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  )

  const dataPoints = sortedLogs.map(log => log.anxiety_level ?? null)

  return { labels, dataPoints }
})

const getLastExposure = () => {
  if (!dish.value || !dish.value.logs || dish.value.logs.length === 0) {
    return 'No exposures yet';
  }

  // Sort logs by date descending
  const sortedLogs = [...dish.value.logs].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const lastLogDate = new Date(sortedLogs[0].date);
  const now = new Date();

  // Calculate difference in days
  const diffTime = now.getTime() - lastLogDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const formattedDate = lastLogDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (diffDays === 0) return `Today - ${formattedDate}`;
  if (diffDays === 1) return `1 day ago - ${formattedDate}`;
  return `${diffDays} days ago - ${formattedDate}`;
};

const toggleAddExposureLogForm = () => {
  showAddExposureLogForm.value = !showAddExposureLogForm.value;
};

const toggleEditDishForm = () => {
  showEditDishForm.value = !showEditDishForm.value;
}

const dishUpdated = (updatedDish: {title: string, description: string, goal: number, list: string}) => {
  if ( dish.value ) {
    dish.value.title = updatedDish.title;
    dish.value.description = updatedDish.description;
    dish.value.goal = updatedDish.goal;
    dish.value.list = updatedDish.list;
  }
}

const logAdded = (new_log: ExposureLog) => {
  if ( dish.value ) {
    dish.value.logs.push({
      ...new_log
    });
  }
};

const dish = ref<Dish | null>(null)

const fetchDish = async (id: string) => {

  loading.value = true
  fetchError.value = null

  try {
    const response = await fetchWithAuth(`${apiUrl}/api/dishes/${id}`, { method: 'GET' }, store)
    if (response.ok) {
      dish.value = await response.json()
    } else {
      fetchError.value = `Failed to fetch dish!`
      console.error('Failed to fetch dish:', await response.text())
    }
    loading.value = false
  } catch (error) {
    loading.value = false
    fetchError.value = `Error fetching dish: ${error instanceof Error ? error.message : 'Unknown error'}`
    console.error('Error fetching dish:', error)
  }
}

onMounted(() => {
  fetchDish(id)
})

const progress = computed(() => {
  if (!dish.value || !dish.value.goal || dish.value.goal === 0) return 0
  return Math.min((dish.value.logs.length / dish.value.goal) * 100, 100)
})

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_router_link, {
        to: "/list/",
        class: "button back"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_font_awesome_icon, {
            icon: ['fas', 'chevron-left'],
            class: "fa-xs"
          }),
          _cache[1] || (_cache[1] = _createTextVNode(" Back "))
        ]),
        _: 1
      }),
      (dish.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("button", {
              id: "edit_dish_button",
              onClick: toggleEditDishForm
            }, [
              _createVNode(_component_font_awesome_icon, { icon: ['fas', 'pen'] })
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    (dish.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", null, [
              _createElementVNode("h1", _hoisted_5, _toDisplayString(dish.value.title), 1),
              (dish.value.description.length > 0)
                ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(dish.value.description), 1))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_7, _toDisplayString(getListName(dish.value.list)), 1)
            ]),
            _createElementVNode("div", null, [
              _cache[2] || (_cache[2] = _createElementVNode("p", { class: "medium" }, "Exposure Goal", -1)),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", {
                  class: _normalizeClass(["exposure_progress_inner", { 'zero-width': progress.value === 0 }]),
                  style: _normalizeStyle({ width: progress.value + '%' })
                }, null, 6)
              ]),
              _createElementVNode("p", _hoisted_9, _toDisplayString(dish.value.logs.length) + " of " + _toDisplayString(dish.value.goal) + " exposures", 1)
            ]),
            (dish.value.logs.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _cache[3] || (_cache[3] = _createElementVNode("p", { class: "medium" }, "Last exposure", -1)),
                  _createElementVNode("p", _hoisted_11, _toDisplayString(getLastExposure()), 1)
                ]))
              : _createCommentVNode("", true),
            (dish.value.logs.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _cache[4] || (_cache[4] = _createElementVNode("p", { class: "medium" }, "Anxiety Level over Time", -1)),
                  _createElementVNode("div", null, [
                    _createVNode(LineChart, {
                      labels: anxietyData.value.labels,
                      dataPoints: anxietyData.value.dataPoints
                    }, null, 8, ["labels", "dataPoints"])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_13, [
                _cache[5] || (_cache[5] = _createElementVNode("p", { class: "medium" }, "Exposure Logs", -1)),
                _createElementVNode("button", {
                  class: "button secondary add_log_button",
                  onClick: toggleAddExposureLogForm
                }, [
                  _createVNode(_component_font_awesome_icon, { icon: ['fas', 'plus'] })
                ])
              ]),
              _createVNode(ExposureList, {
                logs: dish.value.logs,
                dish_id: dish.value.id
              }, null, 8, ["logs", "dish_id"])
            ])
          ]),
          _createVNode(AddExposureLogForm, {
            onLogAdded: logAdded,
            onHideForm: toggleAddExposureLogForm,
            dishId: dish.value.id,
            show: showAddExposureLogForm.value
          }, null, 8, ["dishId", "show"]),
          _createVNode(EditDishForm, {
            onDishUpdated: dishUpdated,
            onHideForm: toggleEditDishForm,
            show: showEditDishForm.value,
            title: dish.value.title,
            description: dish.value.description,
            goal: dish.value.goal,
            dishId: dish.value.id,
            list: dish.value.list
          }, null, 8, ["show", "title", "description", "goal", "dishId", "list"])
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _withDirectives(_createElementVNode("div", _hoisted_14, null, 512), [
            [_vShow, loading.value]
          ]),
          (!dish.value && fetchError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _createElementVNode("p", null, _toDisplayString(fetchError.value), 1),
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (fetchDish(_unref(id))))
                }, "Retry")
              ]))
            : _createCommentVNode("", true)
        ], 64))
  ]))
}
}

})